import React, { useEffect, useRef, useState } from 'react';
import Close from '@mui/icons-material/Close';
import * as S from './Navigation.styled';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  selectIsStaySignedIn,
  selectPassword,
  selectUserEmail
} from 'pages/app/redux';
import { toast } from 'utils/toast';
import {
  selectToken,
  selectLiveView,
  useCreds,
} from 'redux/global';
import { dismissHtmlBootSplashWhenContentReady, ensureHtmlBootSplash, isBootSplashFinished, shouldUseLogoSplash } from 'pages/components/Loader/BootSplash';
import { markBootReveal } from 'pages/components/Loader/bootReveal';
import Loader from 'pages/components/Loader/Loader';
import { getRouteLoadingMessage } from 'pages/components/Loader/getRouteLoadingMessage';
import { getOrganization, organizationFromGetOrganizationData, verifyAuth } from './redux';
import { moveSideBar } from './utils';
import {
  selectDebugText,
  selectOrganizationData,
  selectModalParts,
  selectSocket,
  setOrganizationData,
} from 'pages/redux';
import { useHistory, useLocation } from 'react-router-dom';
import Modal from 'pages/components/Modal/Modal';
import Footer from './Footer/Footer';
import { lFColor2, mobileRes } from 'pages/components/LFHeader/LFHeader.styled';
import { useFetchLogs, useWindowSize } from 'pages/Logs/hooks';
import { usePath } from 'pages/hooks';
import LogsCapacityAlert from './components/LogsCapacityAlert';
import UserSeatsAlert from './components/UserSeatsAlert';
import ManagerReportsAlert, {
  isManagerReportsAlertActive,
} from './components/ManagerReportsAlert';
import LayoutAlertPresence from './components/LayoutAlertPresence';
import { useParentEffects } from './hooks';
import { selectManagerReports } from 'pages/Reporting/redux';
import { useFormsInboxCounts } from 'pages/Forms/formsInboxCountsHook';
import SignInModal from 'pages/SignIn/modals/SignInModal';
import { useFetchSettings } from 'pages/Settings/hooks';
import LFHeader from 'pages/components/LFHeader/LFHeader';
import MobileLogSearchBar from 'pages/Logs/components/MobileLogSearchBar';
import { selectSettingsParts } from 'pages/Settings/redux';
import PageEnter from 'pages/components/PageEnter/PageEnter';
import { logButtonClick } from 'utils/analytics';
import { redirectLiveDemoToTry, shouldExitLiveDemoToTry } from 'utils';
import {
  isErpAppPath,
  isErpPagePath,
} from 'pages/Layout/erpModulePaths';

/** Session-only — live demo banner returns on the next sign-in. */
const LIVE_BANNER_DISMISS_KEY = 'session_dismissed_live_view_banner';

const readLiveBannerDismissed = () => {
  try {
    return sessionStorage.getItem(LIVE_BANNER_DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

const Navigation = function Navigation({ children }:{ children: any}) {
  const [loading, setLoading] = useState<boolean>(false);
  const [isShowSignIn, setIsShowSignIn] = useState<boolean>(false);
  const [currentClient, setCurrentClient] = useState<string | number>('');
  const [sessionBootComplete, setSessionBootComplete] = useState(false);
  const isStaySignedIn = useAppSelector(selectIsStaySignedIn);
  const email = useAppSelector(selectUserEmail);
  const { counts: formsInboxCounts } = useFormsInboxCounts(Boolean(email));
  const password = useAppSelector(selectPassword);
  const token = useAppSelector(selectToken);
  const liveView = useAppSelector(selectLiveView);
  const [liveBannerDismissed, setLiveBannerDismissed] = useState(readLiveBannerDismissed);
  const debugText = useAppSelector(selectDebugText);
  const modalParts = useAppSelector(selectModalParts);
  const organizationData = useAppSelector(selectOrganizationData);
  const socket = useAppSelector(selectSocket);
  const settingsParts = useAppSelector(selectSettingsParts);
  const managerReports = useAppSelector(selectManagerReports);
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  useFetchLogs();
  useFetchSettings();
  const { isHomePath } = usePath();
  const managerReportsAlertActive = isManagerReportsAlertActive(
    managerReports,
    isHomePath,
    email,
    formsInboxCounts.openReportRequests,
  );
  const {
    logout, expireCreds, login, otherTabLogin
  } = useCreds();
  const { width: wWidth } = useWindowSize();
  useParentEffects();

  useEffect(() => {
    const metaTags: { name: string; content: string }[] = [
      { name: 'theme-color', content: lFColor2 },
      { name: 'msapplication-navbutton-color', content: lFColor2 },
      { name: 'apple-mobile-web-app-status-bar-style', content: lFColor2 },
    ];
    metaTags.forEach(({ name, content }) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.name = name;
        document.head.appendChild(el);
      }
      el.content = content;
    });
  }, []);

  const authInFlightRef = useRef(false);

  const authorizeAndLogin = () => {
    const loginUser = async () => {
      setLoading(true);
      const result = await dispatch(login({
        email: email.toLowerCase(),
        password,
        organizationId: localStorage.getItem('organization_data_id') || undefined,
      }));
      if (result.kind === 'ok') {
        const res = await dispatch(getOrganization(result.organizationId, true));
        setLoading(false);
        authInFlightRef.current = false;
        if (res.status === 'success') {
          const organization = organizationFromGetOrganizationData(res.data);
          if (organization) {
            dispatch(setOrganizationData(organization));
            return;
          }
        }
        dispatch(logout());
        toast(res.data, { type: 'error' });
        setIsShowSignIn(true);
        return;
      }
      setLoading(false);
      authInFlightRef.current = false;
      dispatch(logout());
      toast('Could not log in', { type: 'error' });
      setIsShowSignIn(true);
    };

    const fetchOrganizationAndExpireCreds = async (credsExpiry: number) => {
      const res = await dispatch(getOrganization(localStorage.getItem('organization_data_id') || ''));

      setLoading(false);
      authInFlightRef.current = false;
      if (res.status === 'success') {
        const organization = organizationFromGetOrganizationData(res.data);
        if (organization) {
          dispatch(setOrganizationData(organization));
          dispatch(expireCreds(credsExpiry));
          return;
        }
      }
      dispatch(logout());
      if (isStaySignedIn) {
        loginUser();
        return;
      }
      toast(res.data, { type: 'error' });
      setIsShowSignIn(true);
    };

    const authorize = async () => {
      setLoading(true);
      const res = await dispatch(verifyAuth());
      if (res.status === 'success') {
        // Keep loading true through hotel fetch so the session loader does not blink.
        await fetchOrganizationAndExpireCreds(res.data as number);
        return;
      }
      setLoading(false);
      authInFlightRef.current = false;
      if (isStaySignedIn) {
        loginUser();
      } else {
        dispatch(logout());
        setIsShowSignIn(true);
      }
    };

    if (localStorage.getItem('token')) {
      authorize();
    } else {
      authInFlightRef.current = false;
      dispatch(logout());
      setIsShowSignIn(true);
    }
  };

  // Re-read after login; reset when live demo session ends so next sign-in shows it
  useEffect(() => {
    if (!liveView) {
      setLiveBannerDismissed(false);
      return;
    }
    setLiveBannerDismissed(readLiveBannerDismissed());
  }, [liveView]);

  const dismissLiveBanner = () => {
    try {
      sessionStorage.setItem(LIVE_BANNER_DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setLiveBannerDismissed(true);
    logButtonClick('Dismiss live demo banner', { page: 'navigation' });
  };

  const startNewSession = () => {
    const organizationIdLs = localStorage.getItem('organization_data_id');
    const hasToken = Boolean(localStorage.getItem('token'));

    // Socket join — independent of hotel fetch (socket can connect later).
    if (socket && organizationIdLs && currentClient !== organizationIdLs) {
      socket.emit('join_room', `room${organizationIdLs}`);
      setCurrentClient(organizationIdLs);
    }

    // Restore hotel session even if socket is not ready yet.
    // (Previously gated on socket → React 18 race left CRM stuck on loader.)
    if (!organizationData && hasToken && !authInFlightRef.current) {
      authInFlightRef.current = true;
      authorizeAndLogin();
    }
  };

  const isAuthExemptPath = location.pathname.includes('/register')
    || location.pathname.includes('/forgot')
    || location.pathname.includes('/live')
    || location.pathname.includes('/employee-invite')
    || location.pathname === '/form'
    || location.pathname.startsWith('/form/');

  useEffect(() => {
    // Sign-in page owns login — do not also run verify/getOrganization restore there.
    if (!isShowSignIn && !isAuthExemptPath && !isHomePath) {
      startNewSession();
    }
  }, [isShowSignIn, organizationData, socket, currentClient, isAuthExemptPath, isHomePath]);

  useEffect(() => {
    if (!token) {
      // Live demo ended / expired — leave app sign-in for marketing /try (/live exempt).
      if (shouldExitLiveDemoToTry() && !location.pathname.includes('/live')) {
        redirectLiveDemoToTry();
        return;
      }
      setIsShowSignIn(true);
    }
  }, [token, location.pathname]);

  useEffect(() => {
    localStorage.setItem('room_search', '');
    localStorage.setItem('book_search', '');

    const otherTabOperations = (e: StorageEvent) => {
      if (e.key === 'logout_time') {
        const loginAt = localStorage.getItem('login_time');
        const logoutAt = e.newValue || localStorage.getItem('logout_time');
        // Stale logout from a prior failed sign-in — ignore when this tab already logged in.
        if (loginAt && logoutAt && new Date(loginAt) >= new Date(logoutAt)) {
          return;
        }
        dispatch(logout(true));
        // Cross-tab live-demo logout → marketing /try (same as settings Sign out).
        if (shouldExitLiveDemoToTry()) {
          redirectLiveDemoToTry();
          return;
        }
        history.push('/');
      }
      if (e.key === 'login_time') {
        dispatch(otherTabLogin());
        setIsShowSignIn(false);
        setCurrentClient('');
      }
    };
    window.addEventListener('storage', otherTabOperations);

    return () => {
      window.removeEventListener('storage', otherTabOperations);
    };
  }, []);

  const isFitScreen = isErpAppPath(location.pathname);

  const isShowSignInMain = isShowSignIn && !isHomePath;
  const sessionReady = Boolean(organizationData && settingsParts.users && !loading);
  const path = location.pathname;
  const pageDataReady = (() => {
    if (!token || isHomePath || isAuthExemptPath) return true;
    if (!sessionReady) return false;
    // Logs/metrics/dashboard use their own in-page loaders — do not gate the
    // session shell on isLoadingLogs (it flickers as logs/branch-files merge).
    return true;
  })();
  // Latch: once the session has painted ready once, never re-show the shell
  // loader until logout. Stops 1–2 reload blinks from readiness flicker.
  useEffect(() => {
    if (!token) {
      setSessionBootComplete(false);
      return;
    }
    if (sessionReady && pageDataReady) {
      setSessionBootComplete(true);
    }
  }, [token, sessionReady, pageDataReady]);

  const sessionBooting = !isAuthExemptPath && !isHomePath && Boolean(token)
    && !sessionBootComplete
    && (!sessionReady || !pageDataReady);
  // Logo splash only after sign-in; refresh uses the in-app loader.
  const useLogoSplash = shouldUseLogoSplash() && !isBootSplashFinished();
  const showLogoSplash = useLogoSplash && sessionBooting;
  const showAppSessionLoader = !useLogoSplash && sessionBooting;
  const holdChildren = showLogoSplash || showAppSessionLoader;

  const wasBootingRef = useRef(true);
  if (holdChildren) {
    wasBootingRef.current = true;
  } else if (wasBootingRef.current) {
    wasBootingRef.current = false;
    markBootReveal();
  }

  useEffect(() => {
    if (showLogoSplash) {
      ensureHtmlBootSplash();
      return undefined;
    }
    if (useLogoSplash && !sessionBooting) {
      const outer = window.requestAnimationFrame(() => {
        dismissHtmlBootSplashWhenContentReady();
      });
      return () => window.cancelAnimationFrame(outer);
    }
    return undefined;
  }, [showLogoSplash, useLogoSplash, sessionBooting]);

  return (
    <S.MainContainer>
      {
        modalParts && (
          <Modal
            component={modalParts.component}
            exitOnBgClick={modalParts.exitOnBgClick}
            noBg={modalParts.noBg}
            skipSideBar={modalParts.skipSideBar}
          />
        )
      }
      {isHomePath && <LFHeader />}
      <>
        {
          isShowSignInMain ? (
            <>
              <LFHeader marketingChrome />
              <Modal
                component={(
                  <SignInModal onExit={() => setIsShowSignIn(false)} />
                )}
              />
            </>
          ) : (
            <>
              <LayoutAlertPresence
                open={
                  liveView
                  && !isHomePath
                  && !managerReportsAlertActive
                  && !liveBannerDismissed
                }
              >
                <S.LiveViewBanner role="status">
                  Live demo · Summit Lodge is view-only — sign up free to edit your own organization
                  <S.LiveViewBannerDismiss
                    type="button"
                    aria-label="Dismiss"
                    onClick={dismissLiveBanner}
                  >
                    <Close fontSize="small" />
                  </S.LiveViewBannerDismiss>
                </S.LiveViewBanner>
              </LayoutAlertPresence>
              <ManagerReportsAlert />
              <LogsCapacityAlert />
              <UserSeatsAlert />
              {isErpPagePath(location.pathname, 'metrics') && (
                <MobileLogSearchBar
                  breakpoint={mobileRes}
                  aboveHeader
                />
              )}
              {(wWidth || 1051) <= +mobileRes.replace('px', '') && !isHomePath && <LFHeader />}
              {showAppSessionLoader ? <Loader message={getRouteLoadingMessage(path)} /> : null}
              <S.Container id="main_cont" isFitScreen={isFitScreen}>
                <S.Page>
                  <S.DebugBg id="debug_bg">
                    {
                      debugText.map((d, i) => <div key={`debug_${i}`}>{d}</div>)
                    }
                  </S.DebugBg>
                  <S.Bg
                    id="main_bg"
                    onClick={() => {
                      moveSideBar(true);
                    }}
                  />
                  <S.BottomRightPanel isFitScreen={isFitScreen || isHomePath}>
                    {/* Keep routes mounted during session boot so lazy chunks load
                        under the overlay — nulling children forced a second Loader
                        from Suspense after the session loader went away. */}
                    <PageEnter>
                      {children}
                    </PageEnter>
                  </S.BottomRightPanel>
                </S.Page>
                {
                  (isHomePath) && <Footer />
                }
              </S.Container>
            </>
          )
        }
      </>
    </S.MainContainer>
  );
};

export default Navigation;
